import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { Plus, Edit, Trash2, Eye, Power, PowerOff, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../components/UI/Modal';
import MembershipApplicationFormPreview from './MembershipApplicationFormPreview';
import HelpTooltip from '../../components/UI/HelpTooltip';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const FormCard = styled(Card)`
  padding: 1.5rem;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
`;

const FormTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'open') return props.theme.colors.success + '20';
    if (props.status === 'closed') return props.theme.colors.error + '20';
    return props.theme.colors.gray[100];
  }};
  color: ${props => {
    if (props.status === 'open') return props.theme.colors.success;
    if (props.status === 'closed') return props.theme.colors.error;
    return props.theme.colors.text.secondary;
  }};
`;

const FormMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const FormActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MembershipApplicationFormsList = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [previewFormId, setPreviewFormId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const res = await membershipApplicationAPI.getForms();
      setForms(res.data || []);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (form) => {
    try {
      await membershipApplicationAPI.toggleActive(form.id);
      toast.success(`Form ${!form.is_active ? 'activated' : 'deactivated'}`);
      fetchForms();
    } catch (error) {
      toast.error('Failed to update form');
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      const response = await membershipApplicationAPI.deleteForm(deletingId);
      toast.success(response.data?.message || 'Form deleted successfully');
      setShowDeleteModal(false);
      setDeletingId(null);
      fetchForms();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete form';
      toast.error(errorMessage);
      if (error.response?.status === 422) {
        // Form has submissions - close modal but don't refresh
        setShowDeleteModal(false);
        setDeletingId(null);
      }
    }
  };

  const getFormStatus = (form) => {
    if (!form.is_active) return 'inactive';
    const now = new Date();
    if (form.opens_at && new Date(form.opens_at) > now) return 'scheduled';
    if (form.closes_at && new Date(form.closes_at) < now) return 'closed';
    return 'open';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          Membership Forms
          <HelpTooltip>
            <p>This page manages the online forms that applicants use to apply for membership.</p>
            <ul>
              <li><strong>Create Form</strong>: design new public or internal forms for membership applications or recommendations.</li>
              <li><strong>Activate / Deactivate</strong>: control whether a form is currently visible to the public.</li>
              <li><strong>View Submissions</strong>: open the list of applications submitted via a specific form.</li>
              <li><strong>Preview</strong>: see what applicants will see before sharing the form link.</li>
            </ul>
          </HelpTooltip>
        </Title>
        <Button
          variant="primary"
          onClick={() => navigate('/app/membership-applications/forms/create')}
          leftIcon={<Plus size={16} />}
        >
          Create Form
        </Button>
      </Header>

      {loading ? (
        <div>Loading forms...</div>
      ) : forms.length === 0 ? (
        <Card theme={theme} style={{ padding: '3rem', textAlign: 'center' }}>
          <p>No forms created yet. Click "Create Form" to get started.</p>
        </Card>
      ) : (
        <FormsGrid>
          {forms.map((form) => {
            const status = getFormStatus(form);
            return (
              <FormCard key={form.id} theme={theme}>
                <FormHeader>
                  <div style={{ flex: 1 }}>
                    <FormTitle theme={theme}>{form.title}</FormTitle>
                    <StatusBadge status={status} theme={theme}>
                      {status === 'open' && 'Open'}
                      {status === 'closed' && 'Closed'}
                      {status === 'scheduled' && 'Scheduled'}
                      {status === 'inactive' && 'Inactive'}
                    </StatusBadge>
                  </div>
                </FormHeader>

                <FormMeta theme={theme}>
                  <div><strong>Type:</strong> {
                    form.type === 'public' ? 'Public Application' : 
                    form.type === 'recommendation' ? 'Recommendation' : 
                    form.type === 'registration' ? 'Registration' : 
                    form.type
                  }</div>
                  <div><strong>Fields:</strong> {form.activeFields?.length || form.fields?.length || 0}</div>
                  <div><strong>Submissions:</strong> {form.submissions?.length || 0}</div>
                  {form.opens_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={14} />
                      <strong>Opens:</strong> {formatDate(form.opens_at)}
                    </div>
                  )}
                  {form.closes_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={14} />
                      <strong>Closes:</strong> {formatDate(form.closes_at)}
                    </div>
                  )}
                </FormMeta>

                <FormActions>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleToggleActive(form)}
                    leftIcon={form.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                  >
                    {form.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/app/membership-applications/submissions?form_id=${form.id}`)}
                    leftIcon={<Eye size={14} />}
                  >
                    View Submissions
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPreviewFormId(form.id);
                      setShowPreviewModal(true);
                    }}
                    leftIcon={<Eye size={14} />}
                  >
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/app/membership-applications/forms/${form.id}`)}
                    leftIcon={<Eye size={14} />}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/app/membership-applications/forms/${form.id}/edit`)}
                    leftIcon={<Edit size={14} />}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDeletingId(form.id);
                      setShowDeleteModal(true);
                    }}
                    leftIcon={<Trash2 size={14} />}
                  >
                    Delete
                  </Button>
                </FormActions>
              </FormCard>
            );
          })}
        </FormsGrid>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingId(null);
        }}
        title="Delete Form"
      >
        <p>Are you sure you want to delete this form? This action cannot be undone.</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Button variant="outline" onClick={() => {
            setShowDeleteModal(false);
            setDeletingId(null);
          }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewFormId(null);
        }}
        title="Form Preview"
        size="large"
      >
        {previewFormId && (
          <MembershipApplicationFormPreview 
            formId={previewFormId} 
            onClose={() => {
              setShowPreviewModal(false);
              setPreviewFormId(null);
            }} 
          />
        )}
      </Modal>
    </Container>
  );
};

export default MembershipApplicationFormsList;

